import { LegalLayout } from './LegalLayout'
import { COMPANY } from './legalConfig'

export function Terms() {
  const mail = <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>

  return (
    <LegalLayout title="Terms of Service">
      <p className="legal__lede">
        These terms are the agreement between you and {COMPANY.legalName} for use of
        {' '}{COMPANY.tradingName}. Please read section 5 carefully. It sets out what you are
        responsible for as the publisher of anything you produce with this service.
      </p>

      <h2>1. Agreement to these terms</h2>
      <p>
        By creating an account, accessing {COMPANY.site}, or using the {COMPANY.tradingName} apps,
        you agree to these terms. If you are agreeing on behalf of an organisation, you confirm you
        have authority to bind that organisation, and "you" means that organisation.
      </p>
      <p>If you do not agree to these terms, do not use the service.</p>

      <h2>2. Definitions</h2>
      <ul>
        <li><strong>Service</strong> means the {COMPANY.tradingName} website, apps, and daily brief.</li>
        <li><strong>Brief</strong> means the set of stories and draft scripts prepared for you each day.</li>
        <li><strong>Your Content</strong> means recordings and any other material you create or upload.</li>
        <li><strong>Organisation</strong> means the publisher or business whose account you use.</li>
      </ul>

      <h2>3. Eligibility and accounts</h2>
      <p>
        You must be at least 16 years old and able to form a binding contract. Accounts are issued
        to organisations by invitation, and you join one using the invite code you are given.
      </p>
      <p>You agree to:</p>
      <ul>
        <li>give accurate account information and keep it current;</li>
        <li>keep your password confidential and not share your login;</li>
        <li>take responsibility for activity that happens under your account;</li>
        <li>tell us promptly at {mail} if you suspect unauthorised access.</li>
      </ul>
      <p>
        Your Organisation administers your account. It can view content produced under it, add or
        remove users, and close accounts. If you leave the Organisation, it may retain the
        recordings and briefs created during your time there.
      </p>

      <h2>4. What the service does</h2>
      <p>Each day {COMPANY.tradingName}:</p>
      <ul>
        <li>searches publicly available local news sources for the areas you cover;</li>
        <li>ranks what it finds against the topics you have weighted;</li>
        <li>drafts a script for each selected story using an AI language model;</li>
        <li>provides a teleprompter and camera so you can record yourself reading them;</li>
        <li>stores your recordings privately for your Organisation.</li>
      </ul>
      <p>
        We may change, add, or remove features over time. If we remove something significant we
        will give reasonable notice to account holders.
      </p>

      <h2>5. Accuracy, and your responsibility as publisher</h2>
      <p className="legal__callout">
        This is the most important section of these terms. Scripts produced by
        {' '}{COMPANY.tradingName} are unverified first drafts generated by an AI language model.
        You are the publisher, and you are responsible for checking anything before you record or
        post it.
      </p>
      <p>Specifically, you should understand that:</p>
      <ul>
        <li>
          AI language models can misread a source, drop important context, conflate two stories, or
          state something confidently that is simply untrue;
        </li>
        <li>
          source articles can themselves be inaccurate, biased, satirical, paywalled, or out of
          date by the time you read them;
        </li>
        <li>
          automated ranking can miss the most important story of the day, or surface something
          irrelevant or sensitive;
        </li>
        <li>
          a brief is a starting point for your reporting, not a substitute for editorial judgement,
          fact checking, or your own knowledge of your community.
        </li>
      </ul>
      <p>Before you publish anything based on a brief, you agree to:</p>
      <ul>
        <li>follow the source link and read the original;</li>
        <li>verify names, figures, dates, addresses, and quotes independently;</li>
        <li>apply your own editorial standards and any legal duties you are subject to;</li>
        <li>take particular care with stories involving crime, health, minors, or active incidents.</li>
      </ul>
      <p>
        You are solely responsible for what you publish. {COMPANY.legalName} is not a publisher of
        your content and does not review it before you post it.
      </p>

      <h2>6. Your Content</h2>
      <p>
        You own Your Content. We claim no ownership over your recordings or anything you write.
      </p>
      <p>
        You grant us a limited, non exclusive, worldwide, royalty free licence to host, store,
        encode, and transmit Your Content strictly for the purpose of operating the service for
        you. That licence exists so that we can technically deliver the product, and it ends when
        you delete the content or close your account.
      </p>
      <p>
        <strong>
          We do not use Your Content to train machine learning models, we do not sell it, and we do
          not share it with other customers.
        </strong>
      </p>
      <p>
        You confirm that you have the rights needed to record and publish Your Content, including
        permission from anyone who appears in it, and that it does not infringe anyone else's
        rights.
      </p>

      <h2>7. Our intellectual property</h2>
      <p>
        The service, including its software, design, and the {COMPANY.tradingName} name and marks,
        belongs to {COMPANY.legalName} and is protected by intellectual property law. These terms
        grant you a limited, revocable, non transferable right to use the service, and nothing
        more. You may not copy, reverse engineer, resell, or create derivative works from the
        service without our written permission.
      </p>

      <h2>8. Acceptable use</h2>
      <p>You agree not to use {COMPANY.tradingName} to:</p>
      <ul>
        <li>publish anything you know or suspect to be false or misleading;</li>
        <li>harass, threaten, defame, or impersonate any person or organisation;</li>
        <li>infringe copyright, trade marks, privacy, or other rights;</li>
        <li>break any applicable law or regulation;</li>
        <li>attempt to access data belonging to another Organisation;</li>
        <li>probe, scan, or test the security of the service without written permission;</li>
        <li>scrape, crawl, or bulk export content other than your own;</li>
        <li>resell or sublicense the service, or share access outside your Organisation;</li>
        <li>upload malware, or interfere with or overload the service.</li>
      </ul>
      <p>
        We may investigate suspected breaches and may suspend access while we do so.
      </p>

      <h2>9. Third party sources and links</h2>
      <p>
        Briefs reference and link to material published by others. We do not own that material, we
        do not endorse it, and we are not responsible for its accuracy or availability. Your use of
        any third party site is governed by that site's own terms. Always check the original
        source before relying on anything summarised in a brief.
      </p>

      <h2>10. Fees</h2>
      <p>
        Where your Organisation has agreed a paid plan, fees, billing frequency, and any trial
        terms are as set out in that agreement. Unless stated otherwise there, fees are exclusive
        of tax, and payments are non refundable except where the law requires otherwise. We will
        give at least 30 days notice before a price change takes effect.
      </p>

      <h2>11. Availability</h2>
      <p>
        We aim to deliver a brief every morning and to keep the service available, but we do not
        guarantee uninterrupted or error free operation. News sources go offline, third party
        providers have outages, and we occasionally need scheduled maintenance. We will try to
        schedule planned downtime outside peak hours and to give notice where practical.
      </p>

      <h2>12. Disclaimer of warranties</h2>
      <p>
        To the maximum extent permitted by law, the service is provided "as is" and "as available"
        without warranties of any kind, whether express, implied, or statutory, including any
        implied warranties of merchantability, fitness for a particular purpose, non infringement,
        or that content produced by the service will be accurate, complete, or suitable for
        publication.
      </p>
      <p>
        Some jurisdictions do not allow the exclusion of certain warranties, so some of these
        exclusions may not apply to you. Nothing in these terms limits rights you have as a
        consumer that cannot be limited by law.
      </p>

      <h2>13. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, {COMPANY.legalName} and its officers, employees,
        and suppliers will not be liable for:
      </p>
      <ul>
        <li>indirect, incidental, special, consequential, or punitive damages;</li>
        <li>loss of profits, revenue, goodwill, or anticipated savings;</li>
        <li>loss or corruption of data;</li>
        <li>
          claims arising from content you chose to publish, including any inaccuracy in a
          generated script that you did not verify before publishing.
        </li>
      </ul>
      <p>
        Our total aggregate liability arising out of or relating to these terms in any twelve month
        period is limited to the greater of the amount you paid us in that period, or one hundred
        US dollars.
      </p>
      <p>
        Nothing in these terms excludes liability for death or personal injury caused by
        negligence, for fraud, or for anything else that cannot lawfully be excluded.
      </p>

      <h2>14. Indemnity</h2>
      <p>
        You agree to indemnify and hold {COMPANY.legalName} harmless from claims, damages, and
        reasonable legal costs arising from content you publish, your breach of these terms, or
        your infringement of another person's rights.
      </p>

      <h2>15. Suspension and termination</h2>
      <p>
        You may stop using the service at any time and ask us to close your account by emailing
        {' '}{mail}.
      </p>
      <p>
        We may suspend or terminate access if you materially breach these terms, if required by
        law, or if your continued use poses a security or legal risk. Except where a breach is
        serious or unlawful, we will give notice and a reasonable chance to put it right first.
      </p>
      <p>
        On termination your right to use the service ends. You may request an export of Your
        Content within 30 days, after which it may be permanently deleted. Sections 6, 7, 12, 13,
        14, and 17 survive termination.
      </p>

      <h2>16. Changes to these terms</h2>
      <p>
        We may update these terms. We will change the date at the top of this page, and for
        material changes we will notify account holders directly at least 14 days before they take
        effect. If you continue using the service after that, you accept the updated terms. If you
        do not agree, you may close your account.
      </p>

      <h2>17. Governing law and disputes</h2>
      <p>
        These terms are governed by the laws of the State of Oregon, United States, without regard
        to conflict of law rules. You and {COMPANY.legalName} agree to the exclusive jurisdiction
        of the state and federal courts located in Oregon.
      </p>
      <p>
        Before starting formal proceedings, we ask that you contact {mail} so we can try to resolve
        the matter directly. Most issues can be settled quickly that way.
      </p>

      <h2>18. General</h2>
      <ul>
        <li>
          <strong>Entire agreement.</strong> These terms, together with the Privacy Policy and any
          separate written agreement with your Organisation, form the whole agreement between us.
        </li>
        <li>
          <strong>Severability.</strong> If any provision is found unenforceable, the rest remains
          in force.
        </li>
        <li>
          <strong>No waiver.</strong> If we do not enforce a right immediately, we do not give it
          up.
        </li>
        <li>
          <strong>Assignment.</strong> You may not assign these terms without our consent. We may
          assign them to a successor in connection with a merger or sale of the business.
        </li>
        <li>
          <strong>Force majeure.</strong> Neither party is liable for failures caused by events
          beyond reasonable control.
        </li>
      </ul>

      <h2>19. Contact</h2>
      <p>
        Questions about these terms can go to {mail}, or by post to the registered address on our
        {' '}<a href="/contact">contact page</a>. See also our{' '}
        <a href="/privacy">Privacy Policy</a>.
      </p>
    </LegalLayout>
  )
}
